import { ComponentType, createContext, useContext } from "react";

import classnames from "classnames";

import {
  ProductionVolumesColumn,
  ProductionVolumesRow
} from "api/useProductionVolumesQuery";

import { Tooltip } from "components/base";
import { SkeletonBar } from "components/well-card-panel/styles";

import { HeaderContainer } from "./DataTable.styles";
import { ColumnCopyButton } from "./columnCopy";
import { formatCount } from "./monthlyFormat";

export type Row = ProductionVolumesRow;

export const NO_ROWS: never[] = [];

const SKELETON_ROW_COUNT = 8;
export const SKELETON_ROWS: Row[] = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index) => ({ uwi: String(index) })
);

// Kept visible while the first page loads or fails, so the header never disappears.
export const CORE_COLUMNS: ProductionVolumesColumn[] = [
  { key: "uwi", name: "UWI" },
  { key: "month", name: "Month" },
  { key: "source", name: "Production Source" },
  { key: "oil", name: "Oil + Cond (bbl)" },
  { key: "gas", name: "Gas (Mcf)" },
  { key: "water", name: "Water (bbl)" }
];

// Header text is bold in the grid's header font; this per-character allowance plus
// the cell padding and resize handle keeps a header from truncating.
const HEADER_CHAR_WIDTH = 10;
const HEADER_EXTRA_WIDTH = 40;
export const MIN_COLUMN_WIDTH = 120;
// UWI and month are sized for their values, which are wider than their headers.
const VALUE_WIDTHS: Record<string, number> = { uwi: 200, month: 100 };

export const columnWidth = (key: string, name: string) =>
  Math.max(
    VALUE_WIDTHS[key] ?? MIN_COLUMN_WIDTH,
    name.length * HEADER_CHAR_WIDTH + HEADER_EXTRA_WIDTH
  );

const PLANT_LIQUIDS: Record<string, string> = {
  c2: "Ethane",
  c3: "Propane",
  c4: "Butane",
  c5: "Pentanes+"
};
const VOLUME_KEYS = ["oil", "gas", "water", ...Object.keys(PLANT_LIQUIDS)];
// The repeats of these on a well's later rows are muted.
const REPEATED_KEYS = ["uwi", "source"];

export const formatCell = ({ row, column }: { row: Row; column: { key: string } }) => {
  if (SKELETON_ROWS.includes(row)) return <SkeletonBar $width="70%" />;
  const value = row[column.key];
  if (value === null || value === undefined) return null;
  return <>{typeof value === "number" ? formatCount(value) : value}</>;
};

type HeaderProps = { column: { key: string; name: string } };

interface MonthlyCopy {
  // Off for trial and read-only accounts, as in the Data view.
  enabled: boolean;
  copied: Record<string, boolean>;
  onCopyColumn: (name: string, key: string) => void;
}

// Headers are built once per color and tooltip, so the copy state they show comes
// through context rather than through their identity.
export const MonthlyCopyContext = createContext<MonthlyCopy>({
  enabled: false,
  copied: {},
  onCopyColumn: () => undefined
});

const headerRenderers = new Map<string, ComponentType<HeaderProps>>();

// The Data view's frozen-column header: the field's color for the text and the same
// color, tinted, behind it, bleeding over the cell padding, with its copy button. With no
// color (metadata not loaded) the container is unstyled and the grid's default shows.
const headerRenderer = (color?: string, tooltip?: string) => {
  const cacheKey = `${color}|${tooltip}`;
  let renderer = headerRenderers.get(cacheKey);
  if (!renderer) {
    const Header = ({ column }: HeaderProps) => {
      const copy = useContext(MonthlyCopyContext);
      const content = (
        <HeaderContainer
          title={tooltip ? undefined : column.name}
          style={
            color
              ? {
                  color,
                  backgroundColor: `${color}20`,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  position: "fixed",
                  left: "0px",
                  right: "0px",
                  textIndent: "5px"
                }
              : undefined
          }>
          {column.name}
          {copy.enabled && (
            <ColumnCopyButton
              open={copy.copied[column.key] || false}
              onClick={(event) => {
                event.stopPropagation();
                copy.onCopyColumn(column.name, column.key);
              }}
            />
          )}
        </HeaderContainer>
      );
      return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
    };
    renderer = Header;
    headerRenderers.set(cacheKey, renderer);
  }
  return renderer;
};

export interface RowMeta {
  first: boolean;
  alternate: boolean;
}

// Wells alternate, not rows, so every month of a well shares one background.
export const toRowMeta = (rows: Row[]) => {
  const meta = new Map<Row, RowMeta>();
  let wellIndex = -1;
  let previousUwi: unknown;
  rows.forEach((row) => {
    const first = row.uwi !== previousUwi;
    if (first) wellIndex += 1;
    previousUwi = row.uwi;
    meta.set(row, { first, alternate: wellIndex % 2 === 1 });
  });
  return meta;
};

export const toGridColumns = (
  columns: ProductionVolumesColumn[],
  rowMeta: Map<Row, RowMeta>,
  headerColor?: string
) =>
  columns.map(({ key, name }) => {
    const isVolume = VOLUME_KEYS.includes(key);
    return {
      key,
      name,
      resizable: true,
      width: columnWidth(key, name),
      frozen: key === "uwi",
      formatter: formatCell,
      headerRenderer: headerRenderer(headerColor, PLANT_LIQUIDS[key]),
      cellClass: (row: Row) =>
        classnames(
          isVolume && "monthly-number",
          REPEATED_KEYS.includes(key) &&
            rowMeta.get(row)?.first === false &&
            "monthly-muted"
        )
    };
  });

export const rowClass = (rowMeta: Map<Row, RowMeta>) => (row: Row) => {
  const meta = rowMeta.get(row);
  return classnames(
    meta?.first && "monthly-first-row",
    meta?.alternate && "monthly-alternate-well"
  );
};

// Monthly headers wear the Header group's color, like the Data view's entity-name and UWI
// columns, read from the same field metadata.
export const getMonthlyHeaderColor = (
  propertyMap: Record<string, { hexColor?: string }>
) => (propertyMap["Header.EntityName"] ?? propertyMap["Header.UWI"])?.hexColor;
